import useManyRemoteM1 from '@aims/shared/shared/use-many-remote-m1';
import { RedoOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import Column from 'antd/lib/table/Column';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CSPage from '../../components/CSPage';
import CSPageHeader from '../../components/CSPageHeader';
import settings from '../../settings';
import {
  OUTCOME_COLORS,
  allSignInAuditForAdminQuery,
  extractSignInAuditForAdmin,
  signInStatsForAdminQuery,
} from './constants';

const { Text } = Typography;

const OUTCOMES = [
  'SUCCESS',
  'FAILED_PASSWORD',
  'FAILED_2FA',
  'CHALLENGE_SENT',
  'RATE_LIMITED',
  'RESET_REQUIRED',
  'PERMISSION_DENIED',
  'MAGIC_LINK_SUCCESS',
  'GOOGLE_SUCCESS',
  'SIGN_UP',
];

const DAY_MS = 24 * 60 * 60 * 1000;

const IP_HINT =
  'Client-reported via X-Forwarded-For — informational only, and spoofable.';

function SignInActivityPage() {
  const [range, setRange] = useState(() => {
    const before = new Date(Date.now() + DAY_MS);
    const onOrAfter = new Date(Date.now() - 7 * DAY_MS);
    return { onOrAfter: onOrAfter.toISOString(), before: before.toISOString() };
  });

  const filters = useRef({ ...range });
  const sortBy = useRef([{ key: 'createdAt', order: 'DESC' }]);
  const filterChangeTimeout = useRef();

  const {
    loading,
    data: events,
    hasNextPage,
    next,
    refetch,
  } = useManyRemoteM1({
    query: allSignInAuditForAdminQuery,
    extract: extractSignInAuditForAdmin,
    first: settings.querySize,
    filters: filters.current,
    sortBy: sortBy.current,
    fetchPolicy: 'network-only',
    queryId: 'SignInActivityTable',
  });

  const { data: statsData, loading: statsLoading } = useQuery(
    signInStatsForAdminQuery,
    {
      variables: { filters: range },
      fetchPolicy: 'cache-and-network',
    },
  );
  const stats = statsData?.signInStatsForAdmin;

  const onFiltersChanged = useCallback(
    (_changed) => {
      const nextFilters = { ...filters.current };
      if ('dates' in _changed) {
        const dates = _changed.dates;
        const onOrAfter = dates?.[0]
          ? dates[0].toDate().toISOString()
          : undefined;
        const before = dates?.[1] ? dates[1].toDate().toISOString() : undefined;
        nextFilters.onOrAfter = onOrAfter;
        nextFilters.before = before;
        // Always resync: previously a cleared/partial range left the stat cards
        // and flag tables describing a different window than the rows below.
        setRange({ onOrAfter, before });
      }
      if ('outcome' in _changed) {
        nextFilters.outcome = _changed.outcome?.length
          ? _changed.outcome
          : undefined;
      }
      if ('usernameEntered' in _changed) {
        nextFilters.usernameEntered = _changed.usernameEntered || undefined;
      }
      if ('ip' in _changed) {
        nextFilters.ip = _changed.ip || undefined;
      }
      filters.current = nextFilters;
      clearTimeout(filterChangeTimeout.current);
      filterChangeTimeout.current = setTimeout(() => {
        refetch();
      }, 500);
    },
    [refetch],
  );

  // The debounce must not outlive the page: refetch() would dispatch into the
  // query store for a component that no longer exists.
  useEffect(() => () => clearTimeout(filterChangeTimeout.current), []);

  const cappedNote = useCallback(
    (shown, total) =>
      total > shown ? (
        <Text type="secondary">
          Showing the {shown} most recent of {total}. Narrow the date range to
          see the rest.
        </Text>
      ) : null,
    [],
  );

  const dateFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [],
  );

  return (
    <CSPage containerStyle={{ maxWidth: 'unset' }} title="Sign-In Activity">
      <CSPageHeader title="Sign-In Activity" />

      <Form layout="vertical" onValuesChange={onFiltersChanged}>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          <Form.Item
            label="Date Range"
            name="dates"
            style={{ marginRight: 16, minWidth: 300 }}
            extra="Defaults to the last 7 days"
          >
            <DatePicker.RangePicker showTime />
          </Form.Item>
          <Form.Item
            label="Username"
            name="usernameEntered"
            style={{ marginRight: 16, minWidth: 200 }}
          >
            <Input placeholder="Username entered" allowClear />
          </Form.Item>
          <Form.Item
            label="Outcome"
            name="outcome"
            style={{ marginRight: 16, minWidth: 260 }}
          >
            <Select mode="multiple" placeholder="Any" allowClear>
              {OUTCOMES.map((o) => (
                <Select.Option key={o} value={o}>
                  {o}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="IP" name="ip" style={{ minWidth: 180 }}>
            <Input placeholder="IP address" allowClear />
          </Form.Item>
        </div>
      </Form>

      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        <Col xs={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Total Attempts"
              value={stats?.totalAttempts || 0}
            />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={6}>
          <Card size="small" style={{ borderTop: '3px solid #ff4d4f' }}>
            <Statistic
              title="Accounts With Repeated Failures"
              value={stats?.repeatedFailures?.length || 0}
            />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={6}>
          <Card size="small" style={{ borderTop: '3px solid #fa541c' }}>
            <Statistic
              title="Rate-Limited"
              value={stats?.rateLimitedCount || 0}
            />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={6}>
          <Card size="small" style={{ borderTop: '3px solid #fa8c16' }}>
            <Statistic
              title="Failures Then Success"
              value={stats?.failuresThenSuccessCount || 0}
            />
          </Card>
        </Col>
      </Row>

      {stats?.truncated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Showing partial results"
          description="This date range holds more sign-in events than one scan covers.  The counts above are exact, but the two flag tables below cover only the most recent events in the range.  Narrow the range to see all of them."
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Repeated Failures by Account" size="small">
            <Table
              dataSource={stats?.repeatedFailures || []}
              rowKey={(r) => r.usernameEntered || 'unknown'}
              pagination={false}
              size="small"
              loading={statsLoading}
            >
              <Column title="Username" dataIndex="usernameEntered" />
              <Column title="Failures" dataIndex="count" width={100} />
            </Table>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={<Tooltip title={IP_HINT}>Repeated Failures by IP</Tooltip>}
            size="small"
          >
            <Table
              dataSource={stats?.failuresByIp || []}
              rowKey={(r) => r.ip || 'unknown'}
              pagination={false}
              size="small"
              loading={statsLoading}
            >
              <Column title="IP" dataIndex="ip" />
              <Column title="Failures" dataIndex="count" width={100} />
            </Table>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Failures Then Success" size="small">
            <Table
              dataSource={stats?.failuresThenSuccess || []}
              rowKey={(r) => `${r.userId || r.usernameEntered}-${r.createdAt}`}
              pagination={false}
              size="small"
              loading={statsLoading}
            >
              <Column title="Username" dataIndex="usernameEntered" />
              <Column title="Failures Before" dataIndex="failureCount" />
              <Column
                title="Succeeded At"
                dataIndex="createdAt"
                render={(t) => t && dateFormat.format(new Date(t))}
              />
            </Table>
            {cappedNote(
              stats?.failuresThenSuccess?.length || 0,
              stats?.failuresThenSuccessCount || 0,
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={<Tooltip title={IP_HINT}>Sign-Ins From a New IP</Tooltip>}
            size="small"
          >
            {!range.onOrAfter && (
              <Text type="warning">
                This detector needs a start date — without one there is no
                earlier period to call an IP &ldquo;known&rdquo;. Set a date
                range to run it.
              </Text>
            )}
            <Table
              dataSource={stats?.newIpSuccesses || []}
              rowKey={(r) => `${r.userId}-${r.ip}`}
              pagination={false}
              size="small"
              loading={statsLoading}
            >
              <Column title="Username" dataIndex="usernameEntered" />
              <Column title="IP" dataIndex="ip" />
              <Column
                title="When"
                dataIndex="createdAt"
                render={(t) => t && dateFormat.format(new Date(t))}
              />
            </Table>
            {cappedNote(
              stats?.newIpSuccesses?.length || 0,
              stats?.newIpSuccessesCount || 0,
            )}
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 8 }}>
        <Button icon={<RedoOutlined />} onClick={() => refetch()}>
          Refresh
        </Button>
      </Space>
      <Table
        dataSource={events}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: settings.pageSize }}
        scroll={{ x: 1000 }}
      >
        <Column
          title="When"
          dataIndex="createdAt"
          width={190}
          render={(t) => t && dateFormat.format(new Date(t))}
        />
        <Column title="Username" dataIndex="usernameEntered" width={180} />
        <Column title="App" dataIndex="app" width={90} />
        <Column
          title="Outcome"
          dataIndex="outcome"
          width={180}
          render={(t) => <Tag color={OUTCOME_COLORS[t]}>{t}</Tag>}
        />
        <Column
          title={<Tooltip title={IP_HINT}>IP</Tooltip>}
          dataIndex="ip"
          width={150}
        />
        <Column
          title="User Agent"
          dataIndex="userAgent"
          ellipsis
          render={(t) => <Text type="secondary">{t}</Text>}
        />
      </Table>
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        {hasNextPage && (
          <Button onClick={() => next()} loading={loading}>
            Load More
          </Button>
        )}
      </div>
    </CSPage>
  );
}

export default SignInActivityPage;
